import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const EngagementOptions = () => {
  const [selectedOption, setSelectedOption] = useState('consultation');
  const navigate = useNavigate();

  const engagementTypes = [
    {
      id: 'consultation',
      title: 'Strategic Consultation',
      description: 'Book a 45-minute discovery session with our data strategy experts',
      icon: 'Calendar',
      duration: '45 minutes',
      availability: 'Available this week',
      features: [
        'Current state assessment',
        'Strategic roadmap discussion',
        'Technology recommendations',
        'Implementation timeline'
      ],
      cta: 'Schedule Now',
      popular: true
    },
    {
      id: 'proposal',
      title: 'Custom Proposal',
      description: 'Receive a detailed proposal tailored to your specific requirements',
      icon: 'FileText',
      duration: '2-3 business days',
      availability: 'Response guaranteed',
      features: [
        'Detailed scope of work',
        'Investment breakdown',
        'Timeline and milestones',
        'Team allocation'
      ],
      cta: 'Request Proposal'
    },
    {
      id: 'assessment',
      title: 'Data Maturity Assessment',
      description: 'Complete our comprehensive assessment and receive instant insights',
      icon: 'Calculator',
      duration: '15 minutes',
      availability: 'Instant results',
      features: [
        'Current maturity scoring',
        'Gap analysis report',
        'Priority recommendations',
        'Benchmarking insights'
      ],
      cta: 'Start Assessment'
    },
    {
      id: 'webinar',
      title: 'Upcoming Webinar',
      description: 'Join our next thought leadership session on data governance',
      icon: 'Video',
      duration: '60 minutes',
      availability: 'Next: Dec 15, 2025',
      features: [
        'Expert presentations',
        'Live Q&A session',
        'Downloadable resources',
        'Networking opportunities'
      ],
      cta: 'Register Now'
    }
  ];

  const handleEngagement = (type) => {
    switch (type) {
      case 'consultation':
        // Navigate to dedicated consultation page
        navigate('/schedule-consultation');
        break;
      case 'proposal':
        // Navigate to dedicated proposal page
        navigate('/request-proposal');
        break;
      case 'assessment': navigate('/data-maturity-calculator-lead-generation-assessment');
        break;
      case 'webinar':
        // Register for webinar
        window.open('https://northstaranalytics.eu/webinar-registration', '_blank');
        break;
      default:
        break;
    }
  };

  return (
    <section className="py-12 sm:py-16 lg:py-20 bg-background border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header with Icon */}
        <div className="flex items-center justify-center mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center transition-all duration-300 hover:bg-secondary/20 hover:scale-110">
              <Icon name="Grid" size={20} className="text-secondary" />
            </div>
            <span className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wider">Step 1</span>
          </div>
        </div>
        
        <div className="text-center mb-12 sm:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-primary mb-4">
            Choose Your Engagement Path
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-muted-foreground max-w-3xl mx-auto">
            Multiple ways to connect with our expertise. Select the option that best fits your current needs and timeline.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {engagementTypes?.map((option) => (
            <div
              key={option?.id}
              className={`relative bg-card rounded-xl border-2 p-6 cursor-pointer transition-all duration-300 hover:shadow-elevation hover:-translate-y-2 active:scale-95 focus-within:ring-2 focus-within:ring-secondary focus-within:ring-offset-2 ${
                selectedOption === option?.id
                  ? 'border-secondary shadow-elevation scale-105'
                  : 'border-border hover:border-secondary/50'
              }`}
              onClick={() => setSelectedOption(option?.id)}
              tabIndex={0}
              role="button"
              aria-pressed={selectedOption === option?.id}
              onKeyDown={(e) => {
                if (e?.key === 'Enter' || e?.key === ' ') {
                  e?.preventDefault();
                  setSelectedOption(option?.id);
                }
              }}
            >
              {option?.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-secondary text-white px-3 py-1 rounded-full text-xs sm:text-sm font-medium whitespace-nowrap shadow-md hover:shadow-lg transition-shadow duration-300">
                    Most Popular
                  </span>
                </div>
              )}

              <div className="text-center space-y-4">
                <div className={`w-14 h-14 sm:w-16 sm:h-16 mx-auto rounded-full flex items-center justify-center transition-all duration-300 ${
                  selectedOption === option?.id
                    ? 'bg-secondary text-white scale-110 shadow-lg' :'bg-surface text-primary hover:bg-secondary/10 hover:scale-110'
                }`}>
                  <Icon name={option?.icon} size={24} />
                </div>

                <div>
                  <h3 className="text-lg sm:text-xl font-semibold text-primary mb-2 transition-colors duration-300 hover:text-secondary">
                    {option?.title}
                  </h3>
                  <p className="text-muted-foreground text-sm mb-4">
                    {option?.description}
                  </p>
                </div>

                <div className="space-y-2 text-sm">
                  <div className="flex items-center justify-center space-x-2 text-muted-foreground transition-colors duration-300 hover:text-primary">
                    <Icon name="Clock" size={16} className="flex-shrink-0" />
                    <span>{option?.duration}</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2 text-secondary transition-all duration-300 hover:scale-105">
                    <Icon name="CheckCircle" size={16} className="flex-shrink-0" />
                    <span>{option?.availability}</span>
                  </div>
                </div>

                <div className="space-y-2">
                  {option?.features?.map((feature, index) => (
                    <div key={index} className="flex items-start space-x-2 text-sm text-muted-foreground text-left transition-all duration-300 hover:text-primary hover:translate-x-1">
                      <Icon name="Check" size={14} className="text-secondary flex-shrink-0 mt-0.5" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant={selectedOption === option?.id ? "default" : "outline"}
                  fullWidth
                  onClick={(e) => {
                    e?.stopPropagation();
                    handleEngagement(option?.id);
                  }}
                  className={`min-h-[44px] transition-all duration-300 hover:shadow-lg hover:scale-105 active:scale-95 focus:ring-2 focus:ring-secondary focus:ring-offset-2 ${
                    selectedOption === option?.id ? "bg-secondary hover:bg-secondary/90" : "hover:border-secondary hover:text-secondary"
                  }`}
                >
                  {option?.cta}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-12 text-center">
          <p className="text-sm sm:text-base text-muted-foreground mb-4">
            Not sure which option is right for you?
          </p>
          <Button
            variant="ghost"
            iconName="MessageCircle"
            iconPosition="left"
            className="min-h-[44px] transition-all duration-300 hover:bg-secondary/10 hover:text-secondary hover:scale-105 active:scale-95 focus:ring-2 focus:ring-secondary focus:ring-offset-2"
            onClick={() => {
              const element = document.getElementById('contact-form');
              if (element) {
                const offset = window.innerWidth < 768 ? 120 : 100;
                const elementPosition = element?.getBoundingClientRect()?.top + window.pageYOffset;
                const offsetPosition = elementPosition - offset;
                window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
              }
            }}
          >
            Send us a message and we'll guide you
          </Button>
        </div>
      </div>
    </section>
  );
};

export default EngagementOptions;